import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import es from './es';
import en from './en';

export const DEFAULT_LANG = 'es';
const STORAGE_KEY = 'mpcs.lang';

// `locale` drives every Intl call, so dates and numbers switch with the copy.
export const LANGUAGES = [
  { code: 'es', locale: 'es-MX', label: 'Español' },
  { code: 'en', locale: 'en-US', label: 'English' },
];

const DICTS = { es, en };
const localeOf = (code) => (LANGUAGES.find((l) => l.code === code) || LANGUAGES[0]).locale;

function readStored() {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return DICTS[stored] ? stored : DEFAULT_LANG;
  } catch {
    return DEFAULT_LANG;
  }
}

const lookup = (dict, path) =>
  path.split('.').reduce((node, key) => (node == null ? undefined : node[key]), dict);

const interpolate = (template, params) =>
  params
    ? template.replace(/\{(\w+)\}/g, (match, key) =>
        (params[key] === undefined || params[key] === null ? match : String(params[key])))
    : template;

// El PDF se renderiza con pdf() fuera del árbol de React, así que no puede
// usar el hook: no hay proveedor arriba. Esta fábrica da el mismo t() sin
// depender del contexto.
export function makeTranslator(lang = readStored()) {
  const dict = DICTS[lang] || DICTS[DEFAULT_LANG];
  return (path, params) => {
    const value = lookup(dict, path) ?? lookup(DICTS[DEFAULT_LANG], path);
    return typeof value === 'string' ? interpolate(value, params) : path;
  };
}

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(readStored);

  const setLang = useCallback((next) => {
    if (!DICTS[next]) return;
    setLangState(next);
    try { window.localStorage.setItem(STORAGE_KEY, next); } catch { /* modo privado */ }
  }, []);

  useEffect(() => { document.documentElement.lang = lang; }, [lang]);

  const value = useMemo(() => {
    const locale = localeOf(lang);

    // Español es el respaldo: si en.js aún no tiene la clave, se ve el texto
    // en español en vez del path crudo.
    const t = (path, params) => {
      const hit = lookup(DICTS[lang], path) ?? lookup(es, path);
      return typeof hit === 'string' ? interpolate(hit, params) : path;
    };

    const fmt = (value, options) =>
      (value ? new Date(value).toLocaleDateString(locale, options) : t('common.dash'));

    return {
      lang,
      setLang,
      locale,
      languages: LANGUAGES,
      t,
      // Fechas cortas: 09 sep 2026 / 09 Sep 2026
      fmtDate: (v) => fmt(v, { day: '2-digit', month: 'short', year: 'numeric' }),
      fmtDateTime: (v) => (v
        ? new Date(v).toLocaleString(locale, {
            day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
          })
        : t('common.dash')),
      fmtFullDateTime: (v) => (v ? new Date(v).toLocaleString(locale) : t('common.dash')),
      fmtTime: (v) => new Date(v).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' }),
      fmtMonthYear: (v) => fmt(v, { month: 'long', year: 'numeric' }),
      fmtWeekdayShort: (v) => fmt(v, { weekday: 'short' }),
      // Traduce una bandera del backend por código, con el label inglés del
      // servidor como último recurso.
      tFlag: (flag) => {
        const hit = lookup(DICTS[lang], `flags.${flag.code}`) ?? lookup(es, `flags.${flag.code}`);
        return typeof hit === 'string' ? interpolate(hit, flag.params || flag) : flag.label;
      },
      // Errores del API: usa `code` cuando venga, si no el mensaje del servidor.
      tApiError: (err, fallbackKey) => {
        const data = err?.response?.data;
        const hit = data?.code
          ? (lookup(DICTS[lang], `apiErrors.${data.code}`) ?? lookup(es, `apiErrors.${data.code}`))
          : undefined;
        if (typeof hit === 'string') return interpolate(hit, data.params);
        return data?.error || t(fallbackKey);
      },
    };
  }, [lang, setLang]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n debe usarse dentro de <I18nProvider>');
  return ctx;
}
